import React, { type FC, type MouseEvent, useCallback } from 'react';

import { useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled';

import { useAIAnswerContext } from '../../../../ai-answer-context';
import { onRecentWorkItemClick } from '../../../../on-recent-work-item-click';
import { onRecentWorkItemContextMenuClick } from '../../../../on-recent-work-item-context-menu-click';
import { descriptionHelper } from './description-helper/description-helper';
import { RecentWorkItemBody } from './recent-work-item-body';
import { RecentWorkItemByline } from './recent-work-item-byline';
import { RecentWorkItemContainer } from './recent-work-item-container';
import { RecentWorkItemDescription } from './recent-work-item-description';
import { RecentWorkItemIcon } from './recent-work-item-icon';
import { RecentWorkItemLink } from './recent-work-item-link';
import { RecentWorkItemTitle } from './recent-work-item-title';
import { RecentWorkItemWrapper } from './recent-work-item-wrapper';

export type RecentWorkItemProps = {
	index: number;
	title: string;
	iconUrl: string;
	url: string;
	container: string;
	event: string;
	contributors: { id: string; name: string }[];
	timestamp: string;
	othersCount: number;
};

export const RecentWorkItem: FC<RecentWorkItemProps> = ({
	index,
	title,
	iconUrl,
	url,
	container,
	event,
	contributors,
	timestamp,
	othersCount,
}: RecentWorkItemProps) => {
	const { formatMessage, formatDate } = useIntl();
	const {
		userDetails: { id: currentUserId },
		fireAnalyticsEvent,
		commonAttributes,
		onNavigate,
	} = useAIAnswerContext();

	const description = descriptionHelper({
		currentUserId: !!currentUserId ? currentUserId : '',
		event,
		contributors,
		othersCount,
		formatMessage,
		formatDate,
		timestamp: new Date(timestamp),
	});

	const onClick = useCallback(
		(event: MouseEvent) => {
			onNavigate(event);
			fireAnalyticsEvent(onRecentWorkItemClick({ index, url, ...commonAttributes }));
		},
		[onNavigate, fireAnalyticsEvent, index, url, commonAttributes],
	);

	const onContextMenuClick = useCallback(
		(event: MouseEvent) => {
			onNavigate(event);
			fireAnalyticsEvent(onRecentWorkItemContextMenuClick({ index, url, ...commonAttributes }));
		},
		[onNavigate, fireAnalyticsEvent, index, url, commonAttributes],
	);

	return (
		<RecentWorkItemLink href={url} onClick={onClick} onContextMenu={onContextMenuClick}>
			<RecentWorkItemWrapper>
				<RecentWorkItemIcon src={iconUrl} alt="" />
				<RecentWorkItemBody>
					<RecentWorkItemTitle>
						<Text as="p">{title}</Text>
					</RecentWorkItemTitle>
					<RecentWorkItemByline>
						<RecentWorkItemContainer>{container}</RecentWorkItemContainer>
						<RecentWorkItemDescription>{description}</RecentWorkItemDescription>
					</RecentWorkItemByline>
				</RecentWorkItemBody>
			</RecentWorkItemWrapper>
		</RecentWorkItemLink>
	);
};
